import React, { useEffect, useMemo } from "react"
import { Box, Flex, Text, useColorModeValue } from "@chakra-ui/react"
import { useHistory, useParams } from "react-router-dom"
import { useTheme } from "next-themes"

import NotificationDetailPanel from "@/components/Notifications/NotificationDetailPanel"
import { useNotificationsInbox } from "@/contexts/NotificationsInboxContext"
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext"
import {
  getNotificationsCentreCategoryLabel,
  isNotificationsCentreCategory,
  NOTIFICATIONS_CENTRE_BREADCRUMB_LABEL,
  notificationsCentreCategoryNotificationPath,
  notificationsCentreCategoryPath,
  notificationsCentreNotificationPath,
  notificationsCentrePath,
} from "@/constants/notificationsCentre"
import { useShadcnInsetBreadcrumbTrail } from "@/hooks/useShadcnInsetBreadcrumbTrail"
import { useSidebar } from "@/components/ui/sidebar"
import type { BreadcrumbCrumb } from "@/utils/breadcrumbPageTitle"
import { reportDetailSectionCardProps } from "@/views/Dashboard/Reports/components/ReportsCardChrome"
import { statisticsPageMinHeight } from "@/views/Dashboard/Statistics/components/statisticsEmptyState"

export default function NotificationsCentre(): React.ReactElement {
  const history = useHistory()
  const { notificationId, category: categoryParam } = useParams<{
    notificationId?: string
    category?: string
  }>()
  const { isMobile, setOpen } = useSidebar()
  const shadcnInset = useShadcnInsetChrome()
  const pageMinHeight = statisticsPageMinHeight(shadcnInset)
  const {
    notifications,
    categoryFilter,
    fetching,
    loadError,
    selectedNotification,
    reload,
    remove,
    markAsRead,
  } = useNotificationsInbox()

  const { resolvedTheme } = useTheme()
  const subtleText =
    resolvedTheme === "dark" ? "var(--sidebar-muted-foreground)" : "gray.500"

  useEffect(() => {
    if (!isMobile) {
      setOpen(true)
    }
  }, [isMobile, setOpen])

  useEffect(() => {
    if (categoryParam && !isNotificationsCentreCategory(categoryParam)) {
      history.replace(notificationsCentrePath())
    }
  }, [categoryParam, history])

  useEffect(() => {
    if (notificationId) return
    if (notifications.length === 0) return
    const first = notifications[0]
    history.replace(
      categoryFilter
        ? notificationsCentreCategoryNotificationPath(categoryFilter, first.id)
        : notificationsCentreNotificationPath(first.id)
    )
  }, [categoryFilter, history, notificationId, notifications])

  useEffect(() => {
    if (!notificationId || !selectedNotification) return
    if (!selectedNotification.isRead) {
      markAsRead(notificationId)
    }
  }, [markAsRead, notificationId, selectedNotification])

  const shellProps = {
    direction: "column" as const,
    w: "100%",
    minW: 0,
    minH: isMobile ? "auto" : pageMinHeight,
    flex: isMobile ? undefined : "1",
    alignSelf: "stretch" as const,
    className: isMobile ? "min-w-0 w-full" : "min-h-0 min-w-0 flex-1",
  }

  const emptyListLabel = categoryFilter
    ? getNotificationsCentreCategoryLabel(categoryFilter).toLowerCase()
    : null

  const breadcrumbTrail = useMemo((): BreadcrumbCrumb[] | null => {
    // Mobile: keep it short — Notifications › Details (Tessera is already hidden).
    if (isMobile) {
      if (!notificationId) {
        return [{ label: NOTIFICATIONS_CENTRE_BREADCRUMB_LABEL, to: notificationsCentrePath() }]
      }
      return [
        {
          label: NOTIFICATIONS_CENTRE_BREADCRUMB_LABEL,
          to: notificationsCentrePath(),
        },
        { label: "Details" },
      ]
    }

    if (!notificationId) return null

    const trail: BreadcrumbCrumb[] = [
      {
        label: NOTIFICATIONS_CENTRE_BREADCRUMB_LABEL,
        to: notificationsCentrePath(),
      },
    ]
    if (categoryFilter) {
      trail.push({
        label: getNotificationsCentreCategoryLabel(categoryFilter),
        to: notificationsCentreCategoryPath(categoryFilter),
      })
    }
    const title = selectedNotification?.title?.trim()
    trail.push({ label: title || "Details" })
    return trail
  }, [categoryFilter, isMobile, notificationId, selectedNotification?.title])

  useShadcnInsetBreadcrumbTrail(breadcrumbTrail)

  if (loadError && !fetching && notifications.length === 0) {
    return (
      <Flex {...shellProps} align="center" justify="center" gap={3}>
        <Text fontSize="sm" color={subtleText} fontFamily="var(--font-primary)">
          Couldn&apos;t load notifications. Check your connection and try again.
        </Text>
        <button
          type="button"
          className="text-sm text-[color:var(--brand-tropical-surf)]"
          onClick={reload}
        >
          Try again
        </button>
      </Flex>
    )
  }

  if (!fetching && notifications.length === 0) {
    return (
      <Flex {...shellProps} align="center" justify="center">
        <Text fontSize="sm" color={subtleText} fontFamily="var(--font-primary)">
          {emptyListLabel
            ? `No ${emptyListLabel} notifications yet.`
            : "No notifications yet. You're all caught up."}
        </Text>
      </Flex>
    )
  }

  if (notificationId && !selectedNotification && !fetching) {
    return (
      <Flex {...shellProps} align="center" justify="center" direction="column" gap={3}>
        <Text fontSize="sm" color={subtleText} fontFamily="var(--font-primary)">
          That notification is no longer available.
        </Text>
        <button
          type="button"
          className="text-sm text-[color:var(--brand-tropical-surf)]"
          onClick={() =>
            history.push(
              categoryFilter
                ? `/notifications/category/${encodeURIComponent(categoryFilter)}`
                : notificationsCentrePath()
            )
          }
        >
          Back to {categoryFilter ? getNotificationsCentreCategoryLabel(categoryFilter) : "inbox"}
        </button>
      </Flex>
    )
  }

  if (!selectedNotification) {
    if (fetching && notifications.length === 0) {
      return <Flex {...shellProps} minH={0} />
    }

    return (
      <Flex {...shellProps} align="center" justify="center">
        <Text fontSize="sm" color={subtleText} fontFamily="var(--font-primary)">
          Select a notification to view details.
        </Text>
      </Flex>
    )
  }

  return (
    <Flex {...shellProps}>
      <Box
        data-notifications-centre-detail=""
        data-notifications-centre-category={categoryFilter ?? undefined}
        display="flex"
        flex={isMobile ? undefined : "1"}
        minW={0}
        w="100%"
        alignSelf="flex-start"
        flexDirection="column"
        overflow="hidden"
        minH={isMobile ? "auto" : 0}
        {...reportDetailSectionCardProps}
      >
        <NotificationDetailPanel
          notification={selectedNotification}
          variant="page"
          showCloseButton={false}
          onDelete={remove}
        />
      </Box>
    </Flex>
  )
}
